import { useEffect, useState } from 'react';
import { joinTeam, getInviteInfo, InviteInfoResponse } from '../api';

interface JoinTeamPageProps {
  token: string;
  isLoggedIn: boolean;
  lang: 'ko' | 'en';
}

export default function JoinTeamPage({ token, isLoggedIn, lang }: JoinTeamPageProps) {
  const [status, setStatus] = useState<'loading' | 'info' | 'joining' | 'success' | 'error'>('loading');
  const [inviteInfo, setInviteInfo] = useState<InviteInfoResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const t = {
    ko: {
      loading: '초대 정보를 확인하는 중...',
      joining: '팀에 참여하는 중...',
      loginRequired: '로그인이 필요합니다',
      loginRequiredDesc: '팀에 참여하려면 먼저 로그인해주세요.',
      login: '로그인',
      teamInvite: '팀 초대',
      invitedTo: '팀에 초대되었습니다',
      invitedBy: '초대한 사람',
      role: '역할',
      roleOwner: '오너',
      roleManager: '매니저',
      roleMember: '멤버',
      accept: '초대 수락하기',
      decline: '거절',
      expired: '만료된 초대',
      expiredDesc: '이 초대 링크는 만료되었습니다. 팀 관리자에게 다시 초대를 요청하세요.',
      success: '팀에 참여했습니다!',
      goToMain: '메인으로 이동',
      error: '오류가 발생했습니다',
    },
    en: {
      loading: 'Checking invite...',
      joining: 'Joining team...',
      loginRequired: 'Login Required',
      loginRequiredDesc: 'Please log in to join the team.',
      login: 'Login',
      teamInvite: 'Team Invitation',
      invitedTo: 'You are invited to join',
      invitedBy: 'Invited by',
      role: 'Role',
      roleOwner: 'Owner',
      roleManager: 'Manager',
      roleMember: 'Member',
      accept: 'Accept Invitation',
      decline: 'Decline',
      expired: 'Expired Invitation',
      expiredDesc: 'This invitation link has expired. Please ask the team admin to send a new invite.',
      success: 'Successfully joined the team!',
      goToMain: 'Go to Main',
      error: 'An error occurred',
    },
  }[lang];

  const getRoleName = (role: string) => {
    switch (role) {
      case 'owner': return t.roleOwner;
      case 'manager': return t.roleManager;
      case 'member': return t.roleMember;
      default: return role;
    }
  };

  const goToMain = () => {
    window.location.href = '/';
  };

  // 초대 정보 조회
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid invite link');
      return;
    }

    const fetchInviteInfo = async () => {
      try {
        const info = await getInviteInfo(token);
        setInviteInfo(info);
        
        if (info.is_expired) {
          setStatus('error');
          setErrorMessage(t.expiredDesc);
        } else {
          setStatus('info');
        }
      } catch (err: unknown) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to load invite info');
      }
    };

    fetchInviteInfo();
  }, [token, t.expiredDesc]);

  // 초대 수락
  const handleAccept = async () => {
    if (!token) return;
    
    if (!isLoggedIn) {
      // 로그인 필요 - 토큰 저장 후 메인으로 이동 (로그인/가입 후 자동 처리)
      localStorage.setItem('pendingInviteToken', token);
      window.location.href = '/';
      return;
    }

    setStatus('joining');
    
    try {
      const result = await joinTeam(token);
      setSuccessMessage(result.message || t.success);
      setStatus('success');
      
      // 2초 후 메인으로 이동
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (err: unknown) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Failed to join team');
    }
  };

  // 거절 (그냥 메인으로 이동)
  const handleDecline = () => {
    goToMain();
  };

  return (
    <div className="min-h-screen bg-terminal-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* 로딩 */}
        {status === 'loading' && (
          <div className="bg-terminal-card border border-terminal-border rounded-lg p-8 text-center">
            <div className="animate-spin w-10 h-10 border-2 border-status-go border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-terminal-muted">{t.loading}</p>
          </div>
        )}

        {/* 참여 중 */}
        {status === 'joining' && (
          <div className="bg-terminal-card border border-terminal-border rounded-lg p-8 text-center">
            <div className="animate-spin w-10 h-10 border-2 border-status-go border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-terminal-muted">{t.joining}</p>
          </div>
        )}

        {/* 초대 정보 표시 */}
        {status === 'info' && inviteInfo && (
          <div className="bg-terminal-card border border-terminal-border rounded-lg overflow-hidden">
            {/* 헤더 */}
            <div className="bg-terminal-dim/30 px-6 py-4 border-b border-terminal-border">
              <h1 className="text-lg font-semibold text-status-go">{t.teamInvite}</h1>
            </div>

            {/* 컨텐츠 */}
            <div className="p-6">
              {/* 로그인 필요 경고 */}
              {!isLoggedIn && (
                <div className="mb-6 p-4 bg-status-holding/10 border border-status-holding/30 rounded-lg">
                  <p className="text-status-holding text-sm font-medium mb-1">{t.loginRequired}</p>
                  <p className="text-terminal-muted text-xs">{t.loginRequiredDesc}</p>
                </div>
              )}

              {/* 팀 이름 */}
              <div className="text-center mb-6">
                <p className="text-terminal-muted text-sm mb-2">{t.invitedTo}</p>
                <h2 className="text-2xl font-bold text-terminal-text">{inviteInfo.team_name}</h2>
              </div>

              {/* 상세 정보 */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-terminal-border/50">
                  <span className="text-terminal-muted text-sm">{t.invitedBy}</span>
                  <span className="text-terminal-text text-sm">{inviteInfo.invited_by || '-'}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-terminal-muted text-sm">{t.role}</span>
                  <span className="text-terminal-text text-sm font-medium">{getRoleName(inviteInfo.role)}</span>
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex gap-3">
                <button
                  onClick={handleDecline}
                  className="flex-1 px-4 py-3 bg-terminal-dim hover:bg-terminal-dim/80 text-terminal-muted rounded-lg transition-colors"
                >
                  {t.decline}
                </button>
                <button
                  onClick={handleAccept}
                  className="flex-1 px-4 py-3 bg-status-go hover:bg-status-go/90 text-terminal-bg font-semibold rounded-lg transition-colors"
                >
                  {isLoggedIn ? t.accept : t.login}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 성공 */}
        {status === 'success' && (
          <div className="bg-terminal-card border border-terminal-border rounded-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-status-go/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-terminal-text mb-2">{successMessage}</h2>
            <p className="text-terminal-muted text-sm mb-6">{t.goToMain}...</p>
            <button
              onClick={goToMain}
              className="px-6 py-2 bg-status-go hover:bg-status-go/90 text-terminal-bg font-medium rounded-lg transition-colors"
            >
              {t.goToMain}
            </button>
          </div>
        )}

        {/* 에러 */}
        {status === 'error' && (
          <div className="bg-terminal-card border border-terminal-border rounded-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-status-nogo/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-status-nogo" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-terminal-text mb-2">{t.error}</h2>
            <p className="text-terminal-muted text-sm mb-6">{errorMessage}</p>
            <button
              onClick={goToMain}
              className="px-6 py-2 bg-terminal-dim hover:bg-terminal-dim/80 text-terminal-text font-medium rounded-lg transition-colors"
            >
              {t.goToMain}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
